import {
  Component,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { LMBusyIndicatorService, LMCloseOverlayInfo, LMOverlayInfo, LMOverlayService } from '@app/services';
import { LMAdHocSearchComponent, LMDialogInfo, LMFormComponent, LMListComponent, LMPageComponent } from '@app/components';
import { Observable, Subscription } from 'rxjs';
import { LMComponentInjectorService } from './lm-component-injector.service';
import { AppConstants, LMDialogButtonKey, LMGlobalUtilService } from '@app/utils';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';

@Component({
  selector: 'lm-sidebar-resize',
  templateUrl: './lm-sidebar-resize.component.html',
  styleUrl: './lm-sidebar-resize.component.scss',
  standalone: false
})
export class LMSidebarResizeComponent implements OnInit, OnDestroy {

  @ViewChild('dynamic', { read: ViewContainerRef }) viewContainerRef: ViewContainerRef | undefined;
  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective | undefined;

  @HostListener('keydown', [ '$event' ])
  onKeyDown(event: KeyboardEvent) {
    // close the overlay if the escape button is pressed
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.lmOverlayService.close();
    }
  }

  protected defaultWidth = 500;
  protected defaultMaxWidth = 1200;
  protected defaultMouseOffset = 0;
  protected defaultShow = false;
  protected showOverlay = signal(this.defaultShow);
  protected currentWidth = signal(this.defaultWidth);
  protected currentMouseOffset = signal(this.defaultMouseOffset);
  protected currentMinWidth = signal(this.defaultWidth);
  protected currentMaxWidth = signal(this.defaultMaxWidth);

  private overlaySubscription: Subscription | undefined;
  private bannerSubscription: Subscription | undefined;
  private overlayServiceSubscription: Subscription | undefined;
  private busyIndicatorSubscription: Subscription | undefined;
  private width = 0;
  private maxWidth = 1200;
  private unsavedChangesOpen = false;
  private isForm = false;
  private isList = false;
  private isPage = false;
  private isCustom = false;
  private isAdhocSearch = false;
  private _url = '';
  private formComp: LMFormComponent | undefined;
  private listComp: LMListComponent | undefined;
  private pageComp: LMPageComponent | undefined;
  private adhocSearchComp: LMAdHocSearchComponent | undefined;
  private customComp: any | undefined;
  private info: LMDialogInfo = { message: Soho.Locale.translate('UnsavedChangesContinuingMsg') };

  constructor(
    private lmOverlayService: LMOverlayService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    @Inject(LMComponentInjectorService) private lmComponentInjectorService: LMComponentInjectorService,
    private zone: NgZone
  ) {
    this.overlayServiceSubscription = this.lmOverlayService.overlayClose.subscribe((closeInfo: LMCloseOverlayInfo) => {
      if (closeInfo.from === 'service') {
        if (this.isForm) {
          this.handleUnsavedChangesOnForm(this.info)
            // GenAI Generated code - Start
            // LMWEB-26109: report the user's leave/stay decision so a pending navigation guard
            // (LMOverlayService.runOverlayGuard) can resolve, and only close the flyout when the user chose
            // to leave. Previously both branches closed the overlay, so "Cancel/stay" still closed it and
            // the decision was never surfaced to the navigation caller.
            // NOTE: do NOT report on `complete` — the breadcrumb triggers two closes (runOverlayGuard's own
            // close() plus the banner host-click close()), so handleUnsavedChangesOnForm runs twice; the
            // second run hits its unsavedChangesOpen re-entrancy early-out and completes WITHOUT emitting.
            // Reporting on that completion would resolve the guard "leave" and navigate while the first run's
            // dialog is still open. The first run owns the result (via next), so that early-out is a correct
            // no-op. `error` still resolves (as "stay") so a genuinely failed save can't hang the guard.
            .subscribe({
              next: mayLeave => {
                if (this.formComp)
                  this.formComp.saveComponentState(true);
                this.lmOverlayService.reportOverlayGuardResult(mayLeave);
                if (mayLeave && this.lmOverlayService.hasOpenOverlay) {
                  this.close(closeInfo);
                  if (closeInfo.openDetails) {
                    this.lmGlobalUtilService.navigateByUrl(this._url);
                  }
                }
              },
              error: () => this.lmOverlayService.reportOverlayGuardResult(false)
            });
            // GenAI Generated code - End
        } else if (this.isList) {
          this.handleUnsavedChangesOnList(this.info)
            // GenAI Generated code - Start
            // LMWEB-26109: symmetric to the form branch — report leave/stay via next, close only on leave.
            // Do NOT report on `complete` (the re-entrant duplicate close completes without emitting and the
            // first run owns the result — see the form branch note); `error` resolves as "stay" so a failed
            // save can't hang the guard.
            .subscribe({
              next: mayLeave => {
                if (this.listComp)
                  this.listComp.saveComponentState(true);
                this.lmOverlayService.reportOverlayGuardResult(mayLeave);
                if (mayLeave && this.lmOverlayService.hasOpenOverlay) {
                  this.close(closeInfo);
                  if (closeInfo.openDetails) {
                    this.lmGlobalUtilService.navigateByUrl(this._url);
                  }
                }
              },
              error: () => this.lmOverlayService.reportOverlayGuardResult(false)
            });
            // GenAI Generated code - End
        } else if (this.adhocSearchComp || this.isCustom || this.isPage) {
          // GenAI Generated code - Start
          // LMWEB-26109: adhoc-search/custom/page overlays have no unsaved-changes prompt, so a close is
          // always "leave". Still report the result — otherwise a runOverlayGuard awaiting this close (its
          // caller only checks hasOpenOverlay, not content type) would hang the router navigation forever.
          this.lmOverlayService.reportOverlayGuardResult(true);
          // GenAI Generated code - End
          this.close(closeInfo);
          if (closeInfo.openDetails) {
            this.lmGlobalUtilService.navigateByUrl(this._url);
          }
        }
      }
    });

    this.bannerSubscription = this.lmOverlayService.overlayState.subscribe((info: LMOverlayInfo) => {
      if (info.updateUrl && info.url) {
        this.updateOverlayUrl(info.url);
      } else if (info.open) {
        this.openOverlay(info);
      } else {
        this.closeOverlay();
      }
    });

    this.busyIndicatorSubscription = this.lmOverlayService.busyIndicatorSubject.subscribe((show: boolean) => {
      if (show)
        this.showBusyIndicatorInOverlay();
      else
        this.hideBusyIndicatorInOverlay();
    });
  }

  ngOnInit() {
    if (this.viewContainerRef) {
      this.lmComponentInjectorService.setRootViewContainerRef(this.viewContainerRef);
    }
  }

  ngOnDestroy() {
    if (this.overlaySubscription) {
      this.overlaySubscription.unsubscribe();
      this.overlaySubscription = undefined;
    }

    if (this.bannerSubscription) {
      this.bannerSubscription.unsubscribe();
      this.bannerSubscription = undefined;
    }

    if (this.overlayServiceSubscription) {
      this.overlayServiceSubscription.unsubscribe();
      this.overlayServiceSubscription = undefined;
    }
  }

  protected onDragMoved(event: CdkDragMove) {
    const windowWidth = window.innerWidth;

    // event.pointerPosition.x will give horizontal cursor positions to resize the container,
    // but since we are on the right side, we need to subtract from the window width to get
    // the correct width
    if (windowWidth < 500) {
      this.width = windowWidth;
      this.currentMinWidth.set(windowWidth);
    } else {
      this.maxWidth = windowWidth - 340;
      if (this.maxWidth > 1200)
        this.maxWidth = 1200;
      this.width = windowWidth - event.pointerPosition.x;
      if (this.width < 500)
        this.width = 500;
      if (this.width > 1200)
        this.width = 1200;
      this.currentMinWidth.set(this.defaultWidth);
      this.currentMaxWidth.set(this.maxWidth);
    }
    this.currentWidth.set(this.width);

    // CDK drag directive applies a translate3D style to the element, so take that off
    const element = event.source.element.nativeElement;
    element.style.transform = 'none';
  }

  protected onOffSet(event: MouseEvent) {
    // move the drawer handle to where the mouse is on the resizer
    let yOffset = event.pageY - 85;
    if (yOffset < 0)
      yOffset = 0;
    this.currentMouseOffset.set(yOffset);
  }

  protected onFormRendered(): void {
    if (this.width == 0) {
      this.currentWidth.set(500);
      // this.hostWidth = 'auto';
      // this.show = true;
    }
  }

  protected openOverlay(overlayInfo: LMOverlayInfo) {
    if (overlayInfo.replaceCurrentContent) {
      if (this.isForm) {
        this.handleUnsavedChangesOnForm(this.info)
          .subscribe((allowContentUpdate: boolean)  => {
            if (allowContentUpdate ) {
              if (this.formComp)
                this.formComp.saveComponentState(true);
              this.lmComponentInjectorService.destroyComponent();
              this._openOverlay(overlayInfo);
            }
          });
        } else if (this.isList) {
        this.handleUnsavedChangesOnList(this.info)
          .subscribe((allowContentUpdate: boolean)  => {
            if (allowContentUpdate ) {
              if (this.listComp)
                this.listComp.saveComponentState(true);
              this.lmComponentInjectorService.destroyComponent();
              this._openOverlay(overlayInfo);
            }
          });
        } else {
        this.lmComponentInjectorService.destroyComponent();
        this._openOverlay(overlayInfo);
      }
    } else {
      this._openOverlay(overlayInfo);
    }
  }

  protected _openOverlay(overlayInfo: LMOverlayInfo): void {
    if (this.viewContainerRef) {
      const windowWidth = window.innerWidth;

      // event.pointerPosition.x will give horizontal cursor positions to resize the container,
      // but since we are on the right side, we need to subtract from the window width to get
      // the correct width
      if (windowWidth < 500) {
        this.width = windowWidth - 10;
        this.currentMinWidth.set(windowWidth - 10);
        this.currentMaxWidth.set(windowWidth - 10);
        this.currentWidth.set(this.width);
      }

      this.lmComponentInjectorService.setRootViewContainerRef(this.viewContainerRef);

      this.isList = false;
      this.isForm = false;
      this.isPage = false;
      this.isCustom = false;
      this.isAdhocSearch = false;
      switch (overlayInfo.navigationType) {
        case 'list':
          this.isList = true;
          if (overlayInfo.url)
            this.listComp = this.lmComponentInjectorService.addListComponent(overlayInfo.url);
          break;
        case 'page':
          this.isPage = true;
          if (overlayInfo.url)
            this.pageComp = this.lmComponentInjectorService.addPageComponent(overlayInfo.url);
          break;
        case 'custom':
          this.isCustom = true;
          if (overlayInfo.component)
            this.customComp = this.lmComponentInjectorService.addCustomComponent(
              overlayInfo.component,
              overlayInfo.componentInputs
            );
          break;
        case 'adhocSearch':
          this.isAdhocSearch = true;
          this.adhocSearchComp = this.lmComponentInjectorService.addAdhocSearchComponent(overlayInfo);
          break;
        case 'form':
        default:
          this.isForm = true;
          if (overlayInfo.url)
            this.formComp = this.lmComponentInjectorService.addFormComponent(overlayInfo.url);
          break;
      }

      this._url = overlayInfo.url ? overlayInfo.url : '';
      this.showOverlay.set(overlayInfo.open ? overlayInfo.open : false);
      this.lmOverlayService.isOpening = false;

      // need to wait for the content to be loaded before focusing on the
      // first focusable element
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.focusFirstFocusableElement();
        }, 500);
      });
    }
  }

  protected closeOverlay() {
    this.lmOverlayService.resetBackNavigation();
    this.showOverlay.set(false);
    this.isForm = false;
    this.isCustom = false;
    this.lmComponentInjectorService.destroyComponent();
  }

  protected updateOverlayUrl(newUrl: string) {
    this._url = newUrl;
  }

  private handleUnsavedChangesOnForm(info: LMDialogInfo): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.unsavedChangesOpen) {
        observer.complete();
        return;
      }
      if (this.formComp) {
        if (this.formComp.isModified()) {
          this.unsavedChangesOpen = true;
          this.formComp.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
            this.unsavedChangesOpen = false;
            if (this.formComp) {
              if (response === LMDialogButtonKey.YES) {
                this.formComp.runSaveAction(AppConstants.STANDARD_SAVE, observer, false);
              } else if (response === LMDialogButtonKey.NO) {
                // don't save but continue navigation
                observer.next(true);
                observer.complete();
              } else {
                // Cancel. Stay put!
                observer.next(false);
                observer.complete();
              }
            } else {
              // safety, just close without attempting to save
              observer.next(true);
              observer.complete();
            }
          });
        } else {
          // form is not dirty, continue what ever is happening
          observer.next(true);
          observer.complete();
        }
      } else {
        // form isn't open, just close without attempting to save
        observer.next(true);
        observer.complete();
      }
    });
  }

  private handleUnsavedChangesOnList(info: LMDialogInfo): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.unsavedChangesOpen) {
        observer.complete();
        return;
      }
      if (this.listComp) {
        if (this.listComp.listSpec && this.listComp.isModified()) {
          this.unsavedChangesOpen = true;
          this.listComp.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
            this.unsavedChangesOpen = false;
            if (this.listComp) {
              if (response === LMDialogButtonKey.YES) {
                this.listComp.runSaveAction(AppConstants.STANDARD_SAVE, observer, false);
              } else if (response === LMDialogButtonKey.NO) {
                observer.next(true);
                observer.complete();
              } else {
                // Cancel. Stay put!
                observer.next(false);
                observer.complete();
              }
            } else {
              // safety, just close without attempting to save
              observer.next(true);
              observer.complete();
            }
          });
        } else {
          // form is not dirty, continue what ever is happening
          observer.next(true);
          observer.complete();
        }
      } else {
        // form isn't open, just close without attempting to save
        observer.next(true);
        observer.complete();
      }
    });
  }

  private close(closeInfo: LMCloseOverlayInfo): void {
    // close the overlay
    this.showOverlay.set(false);
    this.lmOverlayService.overlayClose.next({
      from: 'component',
      canClose: true,
      openDetails: closeInfo.openDetails
    });
  }

  private showBusyIndicatorInOverlay(): void {
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
  }

  private hideBusyIndicatorInOverlay(): void {
    this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
  }

  private focusFirstFocusableElement() {
    // focus the first non-disabled button in the flyout toolbar
    let contentElement: HTMLElement | null = null;
    if (this.isForm) {
      contentElement = this.formComp?.elementRef.nativeElement;
    } else if (this.isList) {
      contentElement = this.listComp?.elementRef.nativeElement;
    } else if (this.isCustom && this.customComp?.elementRef) {
      contentElement = this.customComp.elementRef.nativeElement;
    } else if (this.isCustom && this.viewContainerRef) {
      contentElement = this.viewContainerRef.element.nativeElement.parentElement;
    }

    if (contentElement) {
      const focusableElements = contentElement.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );

      if (focusableElements.length > 0) {
        (focusableElements.item(0) as HTMLElement).focus();
      }
    }
  }
}
